import React, { useEffect, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { ChatCircleDots, PaperPlaneRight, Sparkle, WarningCircle } from "@phosphor-icons/react";

export default function CoacheeInterviewPage() {
  const [coachee, setCoachee] = useState(null);
  const [session, setSession] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: u } = await api.get("/auth/me");
        const { data: list } = await api.get("/coachees");
        const own = list.find((p) => p.user_id === u.id) || null;
        setCoachee(own);
        if (own) {
          const { data: s } = await api.get(`/coachees/${own.id}/interview`);
          setSession(s);
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    // Bij een nieuw bericht (lang AI-rapport of korte gebruikersvraag):
    // scroll zodat het BEGIN van het laatste bericht bovenaan in het
    // scrollvak staat — de gebruiker leest van boven naar beneden i.p.v.
    // meteen naar de onderkant te springen.
    const last = scrollRef.current.lastElementChild;
    if (last) {
      const containerTop = scrollRef.current.getBoundingClientRect().top;
      const elTop = last.getBoundingClientRect().top;
      scrollRef.current.scrollTop += elTop - containerTop;
    }
  }, [session?.messages?.length]);

  // Houd de cursor knipperend in het tekstvak zodra het beschikbaar is — ook
  // direct na verzenden, zodat de coachee gewoon kan doortikken zonder klikken.
  useEffect(() => {
    if (!sending && !starting && inputRef.current && !inputRef.current.disabled) {
      inputRef.current.focus();
    }
  }, [sending, starting, session?.messages?.length]);

  const handleStart = async () => {
    if (!coachee) return;
    setError("");
    setStarting(true);
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/interview/start`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!coachee || !draft.trim() || sending) return;
    const content = draft.trim();
    setError("");
    setSending(true);
    // optimistic append
    setSession((prev) => ({
      ...(prev || { messages: [], user_message_count: 0, max_user_messages: 60 }),
      messages: [
        ...((prev && prev.messages) || []),
        { role: "user", content, ts: new Date().toISOString() },
      ],
    }));
    setDraft("");
    try {
      const { data } = await api.post(`/coachees/${coachee.id}/interview/message`, { content });
      setSession(data);
    } catch (err) {
      // rollback optimistic add
      setSession((prev) => {
        if (!prev) return prev;
        const msgs = [...(prev.messages || [])];
        // drop the last user message we just appended
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i].role === "user" && msgs[i].content === content) { msgs.splice(i, 1); break; }
        }
        return { ...prev, messages: msgs };
      });
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      // restore draft so the coachee can retry
      setDraft(content);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const used = session?.user_message_count ?? 0;
  const cap = session?.max_user_messages ?? 60;
  const limitReached = session?.limit_reached === true || used >= cap;
  const hasMessages = (session?.messages?.length || 0) > 0;

  return (
    <AppLayout title="Sollicitatiegesprek oefenen">
      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : !coachee ? (
        <div className="text-zinc-500">Coachee-profiel niet gevonden.</div>
      ) : (
        <div className="max-w-3xl space-y-4" data-testid="interview-page">
          <div className="neuro-card-bold p-5 bg-white">
            <div className="flex items-start gap-3">
              <ChatCircleDots size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-display text-lg font-bold tracking-tight">
                  Oefen met de STARR-methode
                </h2>
                <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                  De AI doorloopt met je <strong>twee</strong> situaties: één die mislukte en
                  één die succesvol was. Aan het einde van elke situatie krijg je een
                  samenvatting die je kunt gebruiken in een echt sollicitatiegesprek.
                </p>
                <div className="mt-3 flex items-center gap-3 text-xs text-zinc-500">
                  <span data-testid="interview-counter">
                    Berichten gebruikt: <strong className="text-zinc-900">{used}</strong> / {cap}
                  </span>
                  {limitReached && (
                    <span className="text-[#E53935] font-medium" data-testid="interview-limit-reached">
                      Limiet bereikt — vraag je work coach om te resetten.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div
              className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm flex items-start gap-2"
              data-testid="interview-error"
            >
              <WarningCircle size={18} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div
            ref={scrollRef}
            className="neuro-card p-4 bg-white h-[480px] overflow-y-auto space-y-3"
            data-testid="interview-thread"
          >
            {!hasMessages && !starting && (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <Sparkle size={32} weight="duotone" className="text-[#1E88E5] mb-3" />
                <p className="text-sm text-zinc-600 max-w-sm leading-relaxed">
                  Klaar om te oefenen? Klik op de knop hieronder om het gesprek te starten.
                </p>
                <button
                  type="button"
                  onClick={handleStart}
                  data-testid="interview-start-btn"
                  className="mt-4 inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
                >
                  Start de oefening
                </button>
              </div>
            )}
            {hasMessages && session.messages.map((m, i) => (
              <Bubble key={m.ts ? `${m.ts}-${m.role}` : `i-${i}`} role={m.role} content={m.content} />
            ))}
            {(sending || starting) && <Bubble role="assistant" content="..." typing />}
          </div>

          <form onSubmit={handleSend} className="flex gap-2" data-testid="interview-form">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                limitReached
                  ? "Limiet bereikt. Vraag je work coach om de oefening te resetten."
                  : hasMessages
                    ? "Typ je antwoord..."
                    : "Start eerst de oefening met de knop hierboven."
              }
              disabled={!hasMessages || limitReached || sending}
              data-testid="interview-input"
              rows={2}
              autoFocus
              className="flex-1 px-3 py-2.5 border-2 border-zinc-400 bg-white rounded-sm text-sm shadow-sm caret-zinc-900 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/15 focus:shadow-md disabled:bg-zinc-50 disabled:text-zinc-400 disabled:border-zinc-200 disabled:shadow-none resize-none transition-all"
            />
            <button
              type="submit"
              disabled={!hasMessages || limitReached || sending || !draft.trim()}
              data-testid="interview-send-btn"
              className="self-end inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-300 disabled:cursor-not-allowed"
            >
              <PaperPlaneRight size={16} /> {sending ? "..." : "Verstuur"}
            </button>
          </form>
        </div>
      )}
    </AppLayout>
  );
}

function Bubble({ role, content, typing = false }) {
  const isUser = role === "user";
  return (
    <div
      className={"flex " + (isUser ? "justify-end" : "justify-start")}
      data-testid={isUser ? "interview-msg-user" : "interview-msg-ai"}
    >
      <div
        className={
          "max-w-[80%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap leading-relaxed " +
          (isUser
            ? "bg-zinc-900 text-white"
            : "bg-zinc-100 text-zinc-900 border border-zinc-200")
        }
      >
        {typing ? (
          <span className="inline-flex gap-1 items-end text-zinc-500">
            <Dot delay={0} /><Dot delay={150} /><Dot delay={300} />
          </span>
        ) : isUser ? (
          content
        ) : (
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight prose-p:my-2 prose-li:my-0">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}
